// Reserve-from-stock (3c). Opens right after an order is created. For each order
// line it looks up matching free STOCK in the warehouse (same cable+section+
// conductors) and lets the SALES AGENT choose which to reserve for the order —
// so that length doesn't have to be produced from scratch. Reserving both marks
// the warehouse StorageItem (eng-backend) and records it on the order line
// (redis: line.stockReservedLength) so the order shows "from stock vs to produce".
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, Button, Badge, Form, Alert, Spinner } from 'react-bootstrap';
import { API } from '../../config/api';
import { formatProductLabel } from '../../helpers/productLabel';

const ENG = axios.create({ baseURL: API.BACKEND, timeout: 10000 });
ENG.interceptors.request.use(c => {
  const t = localStorage.getItem('token');
  if (t) c.headers['x-auth-token'] = t;
  return c;
});
const REDIS = axios.create({ baseURL: API.REDIS, timeout: 10000 });
REDIS.interceptors.request.use(c => {
  const t = localStorage.getItem('token');
  if (t) c.headers['x-auth-token'] = t;
  return c;
});

export default function ReserveFromStockModal({ order, show, onHide }) {
  const [matches, setMatches] = useState({}); // lineId -> stock items[]
  const [selected, setSelected] = useState({}); // serial -> { lineId, meters }
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!show || !order) return;
    setSelected({});
    setDone(false);
    setError('');
    setLoading(true);
    (async () => {
      try {
        const out = {};
        for (const line of order.lines || []) {
          const p = line.product || {};
          // match by the full product label ("CYYF 3x2.5") — the warehouse item
          // stores the full label, not just the cable name.
          const label = `${p.cableName || ''} ${p.numberOfConductors || ''}x${p.section || ''}`.trim();
          const url =
            `/api/v1/warehouse/stock?owner=stock&cableName=${encodeURIComponent(label)}`;
          const items = (await ENG.get(url)).data?.data || [];
          out[line.lineId] = items;
        }
        setMatches(out);
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [show, order]);

  const toggle = (lineId, item) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[item.serial]) delete next[item.serial];
      else
        next[item.serial] = {
          lineId,
          serial: item.serial,
          meters: Number(item.lengthMeters) || 0,
          coils: Number(item.coilCount) || 0
        };
      return next;
    });
  };

  const lineSelectedM = lineId =>
    Object.values(selected)
      .filter(s => s.lineId === lineId)
      .reduce((a, s) => a + s.meters, 0);

  const totalSelected = Object.keys(selected).length;

  const reserve = async () => {
    if (!totalSelected) return;
    setBusy(true);
    setError('');
    try {
      const byLine = {};
      for (const [serial, sel] of Object.entries(selected)) {
        (byLine[sel.lineId] = byLine[sel.lineId] || []).push({
          serial,
          meters: sel.meters,
          coils: sel.coils
        });
      }
      for (const [lineId, picks] of Object.entries(byLine)) {
        for (const p of picks) {
          await ENG.post(`/api/v1/warehouse/items/${p.serial}/reserve-sales`, {
            salesOrderId: order.orderId,
            salesOrderCode: order.orderCode,
            customerName: order.client
          });
        }
        await REDIS.post(`/api/redis/orders/${order.orderId}/reserve-from-stock`, {
          lineId,
          meters: picks.reduce((a, p) => a + p.meters, 0),
          serials: picks.map(p => p.serial)
        });
        // auto repack task: >1 pallet reserved → the gestionar should combine them
        if (picks.length > 1) {
          const line = (order.lines || []).find(l => l.lineId === lineId);
          const p = line?.product || {};
          await ENG.post('/api/v1/warehouse/tasks', {
            type: 'repack',
            auto: true,
            title: `Combine ${picks.length} pallets → 1 for ${order.orderCode}`,
            description: `${formatProductLabel(p)} · ${order.client} — consolidate reserved stock into a single pallet`,
            sources: picks.map(pk => ({ serial: pk.serial, coils: pk.coils })),
            forOrderId: order.orderId,
            forOrderCode: order.orderCode,
            customerName: order.client
          }).catch(() => {});
        }
      }
      setDone(true);
      setTimeout(() => onHide(true), 1200);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const anyMatches = Object.values(matches).some(arr => arr.length > 0);

  return (
    <Modal show={show} onHide={() => onHide(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          Reserve from stock — {order?.orderCode} · {order?.client}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {done && <Alert variant="success" className="py-2">Reserved from stock ✓</Alert>}
        {loading ? (
          <div className="py-4 text-center"><Spinner animation="border" size="sm" /> Checking stock…</div>
        ) : !anyMatches ? (
          <p className="text-body-secondary mb-0">
            No matching finished goods in stock — this order will be produced from scratch.
          </p>
        ) : (
          (order?.lines || []).map(line => {
            const p = line.product || {};
            const items = matches[line.lineId] || [];
            const ordered = Number(line.totalOrderedLength) || 0;
            const selM = lineSelectedM(line.lineId);
            return (
              <div key={line.lineId} className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  <strong>{formatProductLabel(p)}</strong>
                  <Badge bg="secondary">ordered {ordered.toLocaleString()} m</Badge>
                  {selM > 0 && (
                    <>
                      <Badge bg="success">{selM.toLocaleString()} m from stock</Badge>
                      <Badge bg="warning" text="dark">
                        {Math.max(0, ordered - selM).toLocaleString()} m to produce
                      </Badge>
                    </>
                  )}
                </div>
                {items.length === 0 ? (
                  <div className="text-body-tertiary fs-9">No stock available for this product.</div>
                ) : (
                  <div className="d-flex flex-column gap-1">
                    {items.map(it => (
                      <label
                        key={it.serial}
                        className="d-flex align-items-center gap-2 border rounded px-2 py-1"
                        style={{ cursor: 'pointer' }}
                      >
                        <Form.Check
                          checked={!!selected[it.serial]}
                          onChange={() => toggle(line.lineId, it)}
                        />
                        <Badge bg={it.type === 'europallet' ? 'primary' : 'warning'}>
                          {it.type === 'europallet' ? 'PALLET' : 'DRUM'}
                        </Badge>
                        <span className="font-monospace fs-10">{it.serial}</span>
                        <span className="ms-auto fw-semibold">
                          {Number(it.lengthMeters || 0).toLocaleString()} m
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => onHide(false)}>
          Skip — produce everything
        </Button>
        <Button variant="primary" onClick={reserve} disabled={busy || !totalSelected}>
          {busy ? 'Reserving…' : `Reserve ${totalSelected || ''} from stock`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
